import Link from 'next/link';
import cn from 'classnames';

import styles from './SeoPagination.module.scss';

type Props = {
  /** Base path without query, e.g. "/athletes" */
  basePath: string;
  /** Current 1-based page */
  currentPage: number;
  /** Total number of items across all pages */
  totalItems?: number;
  /** Items rendered per page */
  perPage: number;
  /** How many numbered links to show on each side of the current page */
  windowSize?: number;
  /** Builds href for a page; defaults to `?page=N` on basePath */
  buildPageHref?: (page: number) => string;
};

const defaultBuildHref = (basePath: string, page: number): string =>
  page <= 1 ? basePath : `${basePath}?page=${page}`;

/**
 * Server-rendered, anchor-based pagination. Unlike the interactive
 * `Pagination` (ReactPaginate, computed in useEffect → renders nothing on the
 * server), every link here is a real `<a href>` present in the SSR HTML so
 * Googlebot can crawl the full listing instead of only the first page that the
 * infinite-scroll grid renders. Also serves as fallback navigation for users.
 */
const SeoPagination = ({
  basePath,
  currentPage,
  totalItems,
  perPage,
  windowSize = 3,
  buildPageHref,
}: Props): JSX.Element | null => {
  const buildHref =
    buildPageHref ?? ((page: number) => defaultBuildHref(basePath, page));
  if (!totalItems || perPage <= 0) {
    return null;
  }

  const pageCount = Math.ceil(totalItems / perPage);
  if (pageCount < 2) {
    return null;
  }

  const page = Math.min(Math.max(currentPage, 1), pageCount);
  const start = Math.max(1, page - windowSize);
  const end = Math.min(pageCount, page + windowSize);

  const pages: number[] = [];
  for (let p = start; p <= end; p += 1) {
    pages.push(p);
  }

  return (
    <nav className={styles.root} aria-label="Pagination">
      {page > 1 && (
        <Link href={buildHref(page - 1)} passHref>
          <a className={styles.link} rel="prev">
            Previous
          </a>
        </Link>
      )}

      {start > 1 && (
        <Link href={buildHref(1)} passHref>
          <a className={styles.link}>1</a>
        </Link>
      )}
      {start > 2 && <span className={styles.ellipsis}>…</span>}

      {pages.map((p) => (
        <Link key={p} href={buildHref(p)} passHref>
          <a
            className={cn(styles.link, { [styles.active]: p === page })}
            aria-current={p === page ? 'page' : undefined}
          >
            {p}
          </a>
        </Link>
      ))}

      {end < pageCount - 1 && <span className={styles.ellipsis}>…</span>}
      {end < pageCount && (
        <Link href={buildHref(pageCount)} passHref>
          <a className={styles.link}>{pageCount}</a>
        </Link>
      )}

      {page < pageCount && (
        <Link href={buildHref(page + 1)} passHref>
          <a className={styles.link} rel="next">
            Next
          </a>
        </Link>
      )}
    </nav>
  );
};

export default SeoPagination;
